import React from "react";
import { Helmet } from "react-helmet-async";
import { buildCanonicalUrl } from "../../utils/basePath";

interface SEOProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  keywords?: string;
  noIndex?: boolean;
  schemaType?:
    | "Organization"
    | "WebSite"
    | "WebPage"
    | "Article"
    | "SoftwareApplication"
    | "Product"
    | "ConfirmationPage";
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  price?: string;
  currency?: string;
  ogImageAlt?: string;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonicalUrl = buildCanonicalUrl("/"),
  keywords = "music app, social music, discord bot, music discovery",
  noIndex = false,
  schemaType = "WebSite",
  ogImage = "https://rythm.fm/assets/images/main-og.png",
  ogType = "website",
  twitterCard = "summary_large_image",
  price,
  currency = "USD",
  ogImageAlt = "Rythm - Discord Music Bot",
}) => {
  // Ensure title has the brand name if it doesn't already
  const fullTitle = title.includes("Rythm") ? title : `${title} | Rythm`;

  // Basic schema.org structured data for the website
  const getJsonLd = () => {
    const baseSchema = {
      "@context": "https://schema.org",
      "@type": schemaType,
      url: canonicalUrl,
      name: fullTitle,
      description: description,
      image: "https://rythm.fm/assets/images/main-og.png",
    };

    // Add specific properties based on schema type
    if (schemaType === "Organization") {
      return {
        ...baseSchema,
        logo: "https://rythm.fm/assets/images/logo.png",
        sameAs: [
          "https://twitter.com/rythmfm",
          "https://www.instagram.com/rythmfm/",
        ],
      };
    } else if (schemaType === "SoftwareApplication") {
      return {
        ...baseSchema,
        applicationCategory: "MusicApplication",
        operatingSystem: "iOS, Android",
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "USD",
        },
      };
    } else if (schemaType === "Product") {
      return {
        ...baseSchema,
        "@type": "Product",
        brand: {
          "@type": "Brand",
          name: "Rythm",
        },
        category: "Music Streaming Service",
        offers: {
          "@type": "Offer",
          price: price || "4.99",
          priceCurrency: currency,
          availability: "https://schema.org/InStock",
          url: canonicalUrl,
          seller: {
            "@type": "Organization",
            name: "Rythm",
          },
        },
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.8",
          reviewCount: "1200",
          bestRating: "5",
          worstRating: "1",
        },
      };
    } else if (schemaType === "ConfirmationPage") {
      return {
        ...baseSchema,
        "@type": "WebPage",
        mainEntity: {
          "@type": "Order",
          orderStatus: "https://schema.org/OrderProcessing",
          orderedItem: {
            "@type": "Product",
            name: "Rythm Premium",
            brand: "Rythm",
          },
        },
      };
    }

    return baseSchema;
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots Meta Tag */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {/* Removed og:image meta tag per requirements */}
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="Rythm" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />
      <meta name="twitter:site" content="@rythmfm" />
      <meta name="twitter:creator" content="@rythmfm" />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(getJsonLd())}</script>
    </Helmet>
  );
};

export default SEO;
